import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { GALLERY, PARTNERS, TESTIMONIALS } from '../mock';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

export function Gallery() {
  const [activeIndex, setActiveIndex] = useState(2); // Start with middle image in focus
  const [isMobile, setIsMobile] = useState(false);
  const [isUserInteracted, setIsUserInteracted] = useState(false);

  // Monitor screen size for responsive card placement offsets
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-play timer that cycles through images every 4.5s
  useEffect(() => {
    if (isUserInteracted) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % GALLERY.length);
    }, 4500);

    return () => clearInterval(interval);
  }, [isUserInteracted]);

  const selectCard = (index) => {
    setIsUserInteracted(true);
    setActiveIndex(index);
  };

  const nextCard = () => {
    setIsUserInteracted(true);
    setActiveIndex((prev) => (prev + 1) % GALLERY.length);
  };

  const prevCard = () => {
    setIsUserInteracted(true);
    setActiveIndex((prev) => (prev - 1 + GALLERY.length) % GALLERY.length);
  };

  // Compute 3D translations, scales, z-indexes, blur, and opacity per card
  const getCardStyle = (index) => {
    let diff = index - activeIndex;

    // Handle wrap-around circular logic for 5 items
    const count = GALLERY.length;
    if (diff < -2) diff += count;
    if (diff > 2) diff -= count;

    let x = 0;
    let scale = 1;
    let zIndex = 10;
    let opacity = 1;
    let filter = 'blur(0px)';

    const offset = isMobile ? 100 : 250; // Dynamic shifts based on device

    if (diff === 0) {
      x = 0;
      scale = 1.06;
      zIndex = 10;
      opacity = 1;
      filter = 'blur(0px)';
    } else if (diff === -1) {
      x = -offset;
      scale = 0.85;
      zIndex = 5;
      opacity = 0.65;
      filter = 'blur(1.5px)';
    } else if (diff === 1) {
      x = offset;
      scale = 0.85;
      zIndex = 5;
      opacity = 0.65;
      filter = 'blur(1.5px)';
    } else {
      // Far Left & Far Right cards (diff === -2 or 2)
      x = diff < 0 ? -offset * 1.7 : offset * 1.7;
      scale = 0.7;
      zIndex = 2;
      opacity = isMobile ? 0 : 0.25; // Hidden on mobile, translucent on desktop
      filter = 'blur(3.5px)';
    }

    return { x, scale, zIndex, opacity, filter };
  };

  return (
    <section className="section-pad bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <h2 className="heading-display text-3xl md:text-5xl text-brand-dark">E Drives in Action</h2>
          <p className="mt-3 text-brand-dark/65 max-w-md mx-auto text-sm">
            Click the side cards or use the controls below to rotate.
          </p>
        </div>
      </div>

      {/* 3D Carousel Stage */}
      <div className="relative h-[320px] md:h-[420px] max-w-5xl mx-auto flex items-center justify-center">
        <div className="relative w-full h-full flex items-center justify-center">
          {GALLERY.map((g, i) => {
            const { x, scale, zIndex, opacity, filter } = getCardStyle(i);
            const isActive = i === activeIndex;

            return (
              <motion.div
                key={i}
                animate={{
                  x: x,
                  scale: scale,
                  opacity: opacity,
                  filter: filter
                }}
                transition={{
                  type: 'spring',
                  stiffness: 110,
                  damping: 18
                }}
                style={{
                  zIndex: zIndex,
                  position: 'absolute',
                }}
                onClick={() => selectCard(i)}
                className={`w-64 md:w-96 p-4 rounded-3xl bg-brand-lavenderSoft border border-brand-dark/5 shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col items-center select-none cursor-pointer ${isActive ? 'cursor-default border-brand-lime/30 shadow-lg' : ''
                  }`}
              >
                <div className={`rounded-2xl overflow-hidden h-36 md:h-60 w-full pointer-events-none shadow-inner ${g.image.includes('action_ops_1') ? 'bg-white' : 'bg-brand-mint'}`}>
                  <img
                    src={g.image}
                    alt={g.caption}
                    className={`w-full h-full pointer-events-none ${g.image.includes('action_ops_1')
                        ? 'object-contain p-2'
                        : g.image.includes('action_street_1')
                          ? 'object-cover object-left-top'
                          : 'object-cover'
                      }`}
                  />
                </div>
                <div className="mt-4 text-center">
                  <p className="font-semibold text-brand-dark heading-display text-base md:text-lg">{g.title}</p>
                  <p className="text-xs md:text-sm text-brand-dark/60 mt-0.5">{g.caption}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Navigation Indicators & Controls */}
      <div className="flex items-center justify-center gap-4 mt-6">
        <button
          onClick={prevCard}
          className="w-10 h-10 rounded-full border border-brand-dark/10 bg-white text-brand-dark flex items-center justify-center hover:bg-brand-lavenderSoft active:scale-95 transition-all shadow-sm"
          aria-label="Previous image"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex gap-2">
          {GALLERY.map((_, i) => (
            <button
              key={i}
              onClick={() => selectCard(i)}
              className={`h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'w-6 bg-brand-green' : 'w-2 bg-brand-dark/20'
                }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        <button
          onClick={nextCard}
          className="w-10 h-10 rounded-full border border-brand-dark/10 bg-white text-brand-dark flex items-center justify-center hover:bg-brand-lavenderSoft active:scale-95 transition-all shadow-sm"
          aria-label="Next image"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </section>
  );
}

export function Trusted() {
  const logos = [
    { name: 'Swiggy', src: '/logo_swiggy.png' },
    { name: 'Amazon Now', src: '/logo_amazon_now.jpg' },
    { name: 'Flipkart Minutes', src: '/logo_flipkart.png' },
    { name: 'Motovolt', src: '/logo_motovolt.jpg' },
    { name: 'Bounce Infinity', src: '/logo_bounce.png' },
    { name: 'Ecofy Autovert', src: '/logo_ecofy.png' },
    { name: 'Bolt.Earth', src: '/logo_bolt_earth.png' },
    { name: 'Ather Grid', src: '/logo_ather.png' },
    { name: 'Sun Mobility', src: '/logo_sun_mobility.png' }
  ];

  return (
    <section id="partners" className="section-pad bg-brand-lavender border-t border-b border-brand-dark/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-14">
          <h2 className="heading-display text-3xl md:text-4xl text-brand-dark">Ecosystem Partners</h2>
          <p className="text-sm text-brand-dark/65 mt-3 max-w-2xl mx-auto leading-relaxed">
            Integrating delivery networks, vehicle &amp; financing partners, and energy infrastructure providers into a unified EV mobility ecosystem.
          </p>
        </div>

        {/* 3D Rotating Sphere/Cylinder Viewport */}
        <div className="relative w-full h-[180px] flex items-center justify-center cylinder-container">
          {/* Side shading to give depth */}
          <div className="absolute left-0 top-0 bottom-0 w-24 sm:w-48 bg-gradient-to-r from-brand-lavender to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 sm:w-48 bg-gradient-to-l from-brand-lavender to-transparent z-10 pointer-events-none" />

          {/* Rotating Cylinder wrapper */}
          <div className="cylinder">
            {logos.map((logo, idx) => (
              <div
                key={logo.name}
                className="cylinder-item bg-white rounded-2xl py-2 px-3 flex items-center justify-center border border-brand-dark/5 shadow-sm hover:scale-105 hover:border-brand-lime/40 transition-all duration-300 cursor-pointer"
                style={{
                  transform: `rotateY(${idx * (360 / logos.length)}deg) translateZ(350px)`
                }}
              >
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="max-w-full max-h-14 object-contain pointer-events-none transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  return (
    <section className="section-pad bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="heading-display text-3xl md:text-5xl text-brand-dark">What People Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-brand-lavenderSoft rounded-3xl p-6 border border-brand-dark/5 hover:-translate-y-1 hover:shadow-lg transition-all">
              <Quote size={20} className="text-brand-green" />
              <div className="flex gap-1 mt-3">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} size={14} className="fill-brand-lime text-brand-lime" />
                ))}
              </div>
              <p className="text-brand-dark/85 mt-3 text-sm leading-relaxed">“{t.quote}”</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-dark text-brand-lime flex items-center justify-center font-semibold text-sm">
                  {t.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <p className="font-semibold text-brand-dark text-sm">{t.name}</p>
                  <p className="text-xs text-brand-dark/60">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
